'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';
import Breadcrumb from '@/components/Breadcrumb';

interface Allarme {
  id: string;
  tipo: string;
  priorita: string;
  stato: string;
  titolo: string;
  descrizione: string;
  dettagli: Record<string, unknown>;
  device_id: string | null;
  reparto_id: string | null;
  zona_id: string | null;
  ip_coinvolti: string[];
  suggerimento_risoluzione: string;
  link_advisory: string;
  cve_id: string;
  soglia_configurata: number | null;
  valore_rilevato: number | null;
  unita_misura: string;
  riconosciuto_da: string | null;
  riconosciuto_at: string | null;
  risolto_da: string | null;
  risolto_at: string | null;
  note_risoluzione: string;
  escalation_at: string | null;
  created_at: string;
  devices?: { hostname: string; ip_address: string } | null;
  reparti?: { nome: string } | null;
  zone_fisiche?: { nome: string } | null;
}

const PRIORITA_CONFIG: Record<string, { label: string; bg: string; text: string; border: string; dot: string }> = {
  critico: { label: 'Critico', bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/40', dot: 'bg-red-500' },
  alto: { label: 'Alto', bg: 'bg-orange-500/15', text: 'text-orange-400', border: 'border-orange-500/40', dot: 'bg-orange-500' },
  medio: { label: 'Medio', bg: 'bg-yellow-500/15', text: 'text-yellow-400', border: 'border-yellow-500/40', dot: 'bg-yellow-500' },
  basso: { label: 'Basso', bg: 'bg-green-500/15', text: 'text-green-400', border: 'border-green-500/40', dot: 'bg-green-500' },
  info: { label: 'Info', bg: 'bg-gray-500/15', text: 'text-gray-400', border: 'border-gray-500/40', dot: 'bg-gray-400' },
};

const STATO_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  attivo: { label: 'Attivo', bg: 'bg-red-500/20', text: 'text-red-400' },
  riconosciuto: { label: 'Riconosciuto', bg: 'bg-orange-500/20', text: 'text-orange-400' },
  in_gestione: { label: 'In Gestione', bg: 'bg-blue-500/20', text: 'text-blue-400' },
  risolto: { label: 'Risolto', bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
  ignorato: { label: 'Ignorato', bg: 'bg-gray-500/20', text: 'text-gray-400' },
  scaduto: { label: 'Scaduto', bg: 'bg-gray-500/20', text: 'text-gray-400' },
};

const TIPO_ICONS: Record<string, string> = {
  dispositivo_offline: 'WifiIcon',
  dispositivo_riconnesso: 'WifiIcon',
  scadenza_manutenzione: 'WrenchScrewdriverIcon',
  scadenza_garanzia: 'ShieldCheckIcon',
  scadenza_licenza: 'DocumentTextIcon',
  scadenza_contratto: 'ClipboardDocumentListIcon',
  conflitto_ip: 'ExclamationTriangleIcon',
  nuovo_dispositivo_non_censito: 'DevicePhoneMobileIcon',
  firmware_obsoleto: 'CpuChipIcon',
  vulnerabilita_cve: 'ShieldExclamationIcon',
  banda_eccessiva: 'ChartBarIcon',
  temperatura_fuori_soglia: 'FireIcon',
  escalation_ticket: 'ArrowUpCircleIcon',
  altro: 'BellAlertIcon',
};

const TIPO_LABELS: Record<string, string> = {
  dispositivo_offline: 'Dispositivo Offline',
  dispositivo_riconnesso: 'Dispositivo Riconnesso',
  scadenza_manutenzione: 'Scadenza Manutenzione',
  scadenza_garanzia: 'Scadenza Garanzia',
  scadenza_licenza: 'Scadenza Licenza',
  scadenza_contratto: 'Scadenza Contratto',
  conflitto_ip: 'Conflitto IP',
  nuovo_dispositivo_non_censito: 'Nuovo Dispositivo',
  firmware_obsoleto: 'Firmware Obsoleto',
  vulnerabilita_cve: 'Vulnerabilità CVE',
  banda_eccessiva: 'Banda Eccessiva',
  temperatura_fuori_soglia: 'Temperatura Anomala',
  escalation_ticket: 'Escalation Ticket',
  altro: 'Altro',
};

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

export default function AllarmiContent() {
  const [allarmi, setAllarmi] = useState<Allarme[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterPriorita, setFilterPriorita] = useState<string>('tutti');
  const [filterStato, setFilterStato] = useState<string>('attivi');
  const [filterTipo, setFilterTipo] = useState<string>('tutti');
  const [selectedAllarme, setSelectedAllarme] = useState<Allarme | null>(null);
  const [noteRisoluzione, setNoteRisoluzione] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showAddAllarme, setShowAddAllarme] = useState(false);
  const [savingAllarme, setSavingAllarme] = useState(false);
  const [formAllarme, setFormAllarme] = useState({ titolo: '', descrizione: '', tipo: 'altro', priorita: 'medio', stato: 'attivo' });

  const fetchAllarmi = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('allarmi')
        .select('*, devices(hostname, ip_address), reparti(nome), zone_fisiche(nome)')
        .order('created_at', { ascending: false });

      if (filterStato === 'attivi') {
        query = query.in('stato', ['attivo', 'riconosciuto', 'in_gestione']);
      } else if (filterStato !== 'tutti') {
        query = query.eq('stato', filterStato);
      }
      if (filterPriorita !== 'tutti') query = query.eq('priorita', filterPriorita);
      if (filterTipo !== 'tutti') query = query.eq('tipo', filterTipo);

      const { data, error: err } = await query;
      if (err) throw err;
      setAllarmi((data as Allarme[]) || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Errore caricamento allarmi');
    } finally {
      setLoading(false);
    }
  }, [filterStato, filterPriorita, filterTipo]);

  useEffect(() => { fetchAllarmi(); }, [fetchAllarmi]);

  const handleAction = async (id: string, nuovoStato: string) => {
    setActionLoading(true);
    try {
      const updates: Record<string, unknown> = { stato: nuovoStato };
      if (nuovoStato === 'riconosciuto') updates.riconosciuto_at = new Date().toISOString();
      if (nuovoStato === 'risolto') {
        updates.risolto_at = new Date().toISOString();
        updates.note_risoluzione = noteRisoluzione;
      }
      const { error: err } = await supabase.from('allarmi').update(updates).eq('id', id);
      if (err) throw err;

      // Log evento
      await supabase.from('storico_eventi').insert({
        tipo: nuovoStato === 'risolto' ? 'allarme_risolto' : 'allarme_riconosciuto',
        titolo: `Allarme ${nuovoStato === 'risolto' ? 'risolto' : 'riconosciuto'}`,
        descrizione: selectedAllarme?.titolo || '',
        allarme_id: id,
        severita: selectedAllarme?.priorita || 'info',
      });

      setSelectedAllarme(null);
      setNoteRisoluzione('');
      await fetchAllarmi();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Errore aggiornamento');
    } finally {
      setActionLoading(false);
    }
  };

  const creaAllarme = async () => {
    if (!formAllarme.titolo.trim()) { alert('Titolo obbligatorio'); return; }
    setSavingAllarme(true);
    try {
      const { error: err } = await supabase.from('allarmi').insert({
        titolo: formAllarme.titolo,
        descrizione: formAllarme.descrizione,
        tipo: formAllarme.tipo,
        priorita: formAllarme.priorita,
        stato: formAllarme.stato,
      });
      if (err) throw err;
      setShowAddAllarme(false);
      setFormAllarme({ titolo: '', descrizione: '', tipo: 'altro', priorita: 'medio', stato: 'attivo' });
      fetchAllarmi();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Errore creazione allarme');
    } finally {
      setSavingAllarme(false);
    }
  };

  const counts = {
    critico: allarmi.filter(a => a.priorita === 'critico' && ['attivo','riconosciuto','in_gestione'].includes(a.stato)).length,
    alto: allarmi.filter(a => a.priorita === 'alto' && ['attivo','riconosciuto','in_gestione'].includes(a.stato)).length,
    medio: allarmi.filter(a => a.priorita === 'medio' && ['attivo','riconosciuto','in_gestione'].includes(a.stato)).length,
    basso: allarmi.filter(a => a.priorita === 'basso' && ['attivo','riconosciuto','in_gestione'].includes(a.stato)).length,
  };

  const formatDate = (d: string) => new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const timeAgo = (d: string) => {
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins} min fa`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} ore fa`;
    return `${Math.floor(hrs / 24)} giorni fa`;
  };

  return (
    <>
      <div className="p-6 space-y-6">
        {/* Breadcrumb */}
        <Breadcrumb items={[{ label: 'Dashboard Allarmi' }]} />
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Icon name="BellAlertIcon" size={28} className="text-red-400" />
              Dashboard Allarmi
            </h1>
            <p className="text-muted-color text-sm mt-1">Monitoraggio allarmi attivi con priorità visiva</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchAllarmi} className="flex items-center gap-2 px-4 py-2 bg-sky-500/10 text-sky-400 rounded-lg hover:bg-sky-500/20 transition-colors text-sm font-semibold">
              <Icon name="ArrowPathIcon" size={16} />
              Aggiorna
            </button>
            <button onClick={() => setShowAddAllarme(true)} className="flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm font-semibold border border-red-500/20">
              <Icon name="PlusIcon" size={16} />
              Crea Allarme
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { key: 'critico', label: 'Critici', icon: 'FireIcon' },
            { key: 'alto', label: 'Alti', icon: 'ExclamationTriangleIcon' },
            { key: 'medio', label: 'Medi', icon: 'ExclamationCircleIcon' },
            { key: 'basso', label: 'Bassi', icon: 'InformationCircleIcon' },
          ].map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => setFilterPriorita(filterPriorita === key ? 'tutti' : key)}
              className={`p-4 rounded-xl border transition-all text-left ${
                filterPriorita === key
                  ? `${PRIORITA_CONFIG[key].bg} ${PRIORITA_CONFIG[key].border} border`
                  : 'bg-surface border-border hover:border-gray-600'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <Icon name={icon as any} size={20} className={PRIORITA_CONFIG[key].text} />
                <span className={`w-3 h-3 rounded-full ${PRIORITA_CONFIG[key].dot} ${counts[key as keyof typeof counts] > 0 ? 'animate-pulse' : ''}`} />
              </div>
              <div className={`text-3xl font-bold ${PRIORITA_CONFIG[key].text}`}>{counts[key as keyof typeof counts]}</div>
              <div className="text-xs text-muted-color mt-1">{label}</div>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="flex gap-1 bg-surface rounded-lg p-1 border border-border">
            {[
              { v: 'attivi', l: 'Attivi' },
              { v: 'tutti', l: 'Tutti' },
              { v: 'risolto', l: 'Risolti' },
            ].map(({ v, l }) => (
              <button key={v} onClick={() => setFilterStato(v)}
                className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${filterStato === v ? 'bg-sky-500/20 text-sky-400' : 'text-muted-color hover:text-white'}`}>
                {l}
              </button>
            ))}
          </div>
          <select value={filterTipo} onChange={e => setFilterTipo(e.target.value)}
            className="bg-surface border border-border rounded-lg px-3 py-1.5 text-sm text-white">
            <option value="tutti">Tutti i tipi</option>
            {Object.entries(TIPO_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>

        {/* Allarmi List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400">{error}</div>
        ) : allarmi.length === 0 ? (
          <div className="text-center py-16 text-muted-color">
            <Icon name="CheckCircleIcon" size={48} className="mx-auto mb-3 text-emerald-500/50" />
            <p className="text-lg font-semibold text-white">Nessun allarme attivo</p>
            <p className="text-sm mt-1">Tutti i sistemi funzionano correttamente</p>
          </div>
        ) : (
          <div className="space-y-3">
            {allarmi.map(allarme => {
              const p = PRIORITA_CONFIG[allarme.priorita] || PRIORITA_CONFIG.info;
              const s = STATO_CONFIG[allarme.stato] || STATO_CONFIG.attivo;
              const icon = TIPO_ICONS[allarme.tipo] || 'BellAlertIcon';
              return (
                <div key={allarme.id}
                  className={`rounded-xl border p-4 transition-all hover:border-gray-600 cursor-pointer ${p.bg} ${p.border}`}
                  onClick={() => setSelectedAllarme(allarme)}>
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${p.bg}`}>
                      <Icon name={icon as any} size={20} className={p.text} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${p.dot} ${allarme.stato === 'attivo' ? 'animate-pulse' : ''}`} />
                        <h3 className="font-semibold text-white truncate">{allarme.titolo}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${p.bg} ${p.text}`}>{p.label}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${s.bg} ${s.text}`}>{s.label}</span>
                        {allarme.cve_id && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 font-mono">{allarme.cve_id}</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-color mt-1 line-clamp-1">{allarme.descrizione}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-color flex-wrap">
                        <span className="flex items-center gap-1">
                          <Icon name="TagIcon" size={12} />
                          {TIPO_LABELS[allarme.tipo] || allarme.tipo}
                        </span>
                        {allarme.devices && (
                          <span className="flex items-center gap-1">
                            <Icon name="ServerIcon" size={12} />
                            {allarme.devices.hostname}
                          </span>
                        )}
                        {allarme.reparti && (
                          <span className="flex items-center gap-1">
                            <Icon name="BuildingOffice2Icon" size={12} />
                            {allarme.reparti.nome}
                          </span>
                        )}
                        <span className="flex items-center gap-1 ml-auto">
                          <Icon name="ClockIcon" size={12} />
                          {timeAgo(allarme.created_at)}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {allarme.stato === 'attivo' && (
                        <button onClick={e => { e.stopPropagation(); handleAction(allarme.id, 'riconosciuto'); }}
                          className="px-3 py-1.5 bg-orange-500/20 text-orange-400 rounded-lg text-xs font-semibold hover:bg-orange-500/30 transition-colors">
                          Riconosci
                        </button>
                      )}
                      {['attivo','riconosciuto','in_gestione'].includes(allarme.stato) && (
                        <button onClick={e => { e.stopPropagation(); setSelectedAllarme(allarme); }}
                          className="px-3 py-1.5 bg-emerald-500/20 text-emerald-400 rounded-lg text-xs font-semibold hover:bg-emerald-500/30 transition-colors">
                          Risolvi
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedAllarme && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedAllarme(null)}>
          <div className="bg-surface border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-border flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-3 h-3 rounded-full ${PRIORITA_CONFIG[selectedAllarme.priorita]?.dot || 'bg-gray-400'}`} />
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${PRIORITA_CONFIG[selectedAllarme.priorita]?.bg} ${PRIORITA_CONFIG[selectedAllarme.priorita]?.text}`}>
                    {PRIORITA_CONFIG[selectedAllarme.priorita]?.label}
                  </span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATO_CONFIG[selectedAllarme.stato]?.bg} ${STATO_CONFIG[selectedAllarme.stato]?.text}`}>
                    {STATO_CONFIG[selectedAllarme.stato]?.label}
                  </span>
                </div>
                <h2 className="text-xl font-bold text-white">{selectedAllarme.titolo}</h2>
                <p className="text-sm text-muted-color mt-1">{formatDate(selectedAllarme.created_at)}</p>
              </div>
              <button onClick={() => setSelectedAllarme(null)} className="p-2 hover:bg-surface-elevated rounded-lg transition-colors">
                <Icon name="XMarkIcon" size={20} className="text-muted-color" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-xs text-muted-color uppercase tracking-wider mb-1">Descrizione</p>
                <p className="text-white text-sm">{selectedAllarme.descrizione}</p>
              </div>
              {selectedAllarme.suggerimento_risoluzione && (
                <div className="bg-sky-500/10 border border-sky-500/30 rounded-xl p-4">
                  <p className="text-xs text-sky-400 font-semibold mb-1 flex items-center gap-1">
                    <Icon name="LightBulbIcon" size={14} />
                    Suggerimento Risoluzione
                  </p>
                  <p className="text-sm text-white">{selectedAllarme.suggerimento_risoluzione}</p>
                </div>
              )}
              {selectedAllarme.cve_id && (
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                  <p className="text-xs text-purple-400 font-semibold mb-1">CVE: {selectedAllarme.cve_id}</p>
                  {selectedAllarme.link_advisory && (
                    <a href={selectedAllarme.link_advisory} target="_blank" rel="noopener noreferrer"
                      className="text-sm text-sky-400 hover:underline flex items-center gap-1">
                      <Icon name="ArrowTopRightOnSquareIcon" size={14} />
                      Visualizza Advisory
                    </a>
                  )}
                </div>
              )}
              {selectedAllarme.ip_coinvolti?.length > 0 && (
                <div>
                  <p className="text-xs text-muted-color uppercase tracking-wider mb-2">IP Coinvolti</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedAllarme.ip_coinvolti.map(ip => (
                      <span key={ip} className="px-2 py-1 bg-surface-elevated rounded-lg text-sm font-mono text-white">{ip}</span>
                    ))}
                  </div>
                </div>
              )}
              {selectedAllarme.valore_rilevato !== null && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-surface-elevated rounded-xl p-3">
                    <p className="text-xs text-muted-color mb-1">Valore Rilevato</p>
                    <p className="text-xl font-bold text-red-400">{selectedAllarme.valore_rilevato} {selectedAllarme.unita_misura}</p>
                  </div>
                  {selectedAllarme.soglia_configurata !== null && (
                    <div className="bg-surface-elevated rounded-xl p-3">
                      <p className="text-xs text-muted-color mb-1">Soglia Configurata</p>
                      <p className="text-xl font-bold text-emerald-400">{selectedAllarme.soglia_configurata} {selectedAllarme.unita_misura}</p>
                    </div>
                  )}
                </div>
              )}
              {['attivo','riconosciuto','in_gestione'].includes(selectedAllarme.stato) && (
                <div className="border-t border-border pt-4">
                  <p className="text-sm font-semibold text-white mb-2">Risolvi Allarme</p>
                  <textarea
                    value={noteRisoluzione}
                    onChange={e => setNoteRisoluzione(e.target.value)}
                    placeholder="Note di risoluzione (opzionale)..."
                    className="w-full bg-surface-elevated border border-border rounded-xl p-3 text-sm text-white placeholder-muted-color resize-none h-20 focus:outline-none focus:border-sky-500"
                  />
                  <div className="flex gap-3 mt-3">
                    {selectedAllarme.stato === 'attivo' && (
                      <button onClick={() => handleAction(selectedAllarme.id, 'riconosciuto')} disabled={actionLoading}
                        className="flex-1 py-2 bg-orange-500/20 text-orange-400 rounded-xl font-semibold text-sm hover:bg-orange-500/30 transition-colors disabled:opacity-50">
                        Riconosci
                      </button>
                    )}
                    <button onClick={() => handleAction(selectedAllarme.id, 'risolto')} disabled={actionLoading}
                      className="flex-1 py-2 bg-emerald-500/20 text-emerald-400 rounded-xl font-semibold text-sm hover:bg-emerald-500/30 transition-colors disabled:opacity-50">
                      {actionLoading ? 'Salvataggio...' : 'Segna come Risolto'}
                    </button>
                    <button onClick={() => handleAction(selectedAllarme.id, 'ignorato')} disabled={actionLoading}
                      className="px-4 py-2 bg-gray-500/20 text-gray-400 rounded-xl font-semibold text-sm hover:bg-gray-500/30 transition-colors disabled:opacity-50">
                      Ignora
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Alarm Modal */}
      {showAddAllarme && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-white">Crea Allarme Manuale</h3>
              <button onClick={() => setShowAddAllarme(false)} className="text-muted-color hover:text-white"><Icon name="XMarkIcon" size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-muted-color mb-1">Titolo *</label>
                <input value={formAllarme.titolo} onChange={e => setFormAllarme(p => ({ ...p, titolo: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-color mb-1">Descrizione</label>
                <textarea value={formAllarme.descrizione} onChange={e => setFormAllarme(p => ({ ...p, descrizione: e.target.value }))} rows={2} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500 resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Tipo</label>
                  <select value={formAllarme.tipo} onChange={e => setFormAllarme(p => ({ ...p, tipo: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500">
                    {Object.keys(TIPO_LABELS).map(t => <option key={t} value={t}>{TIPO_LABELS[t]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Priorità</label>
                  <select value={formAllarme.priorita} onChange={e => setFormAllarme(p => ({ ...p, priorita: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500">
                    {Object.keys(PRIORITA_CONFIG).map(p => <option key={p} value={p}>{PRIORITA_CONFIG[p].label}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <button onClick={() => setShowAddAllarme(false)} className="flex-1 px-4 py-2 border border-border text-secondary-color rounded-lg hover:bg-surface-elevated transition-colors text-sm">Annulla</button>
              <button onClick={creaAllarme} disabled={savingAllarme} className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-semibold disabled:opacity-50">
                {savingAllarme ? 'Creazione...' : 'Crea Allarme'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
